import React, { useEffect, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import axios from 'axios';
import ShowPageWrapper from '../components/ShowPageWrapper';
import StoryContent from '../components/textEditor/StoryContent';
import CurrentlyEditingUsers from '../components/CurrentlyEditingUsers/index';

export default function ShowPage({ appData }) {
  const { storyId, yWebsocketUrl, jwt, customFields } = JSON.parse(appData);

  /**
   * Callback to be applied to edit buttons on the page to verify that the y-websocket server
   * is up and running before the user's redirected to an editor screen
   */
  const checkContentServerBeforeEditing = useCallback(async event => {
    // Check if the button is disabled or has the specific tooltip text
    if (
      event.target.classList.contains('disabled') ||
      event.target.getAttribute('data-tooltip-text') === 'Remove the story from on hold to edit.'
    ) {
      return false; // Quit any further actions
    }

    // Block any default behavior before we've checked the server
    event.preventDefault();
    event.stopImmediatePropagation();

    // Disable the button while we're waiting for the callback to call back
    event.target.classList.add('disabled');
    event.target.classList.add('disabled-spinner');

    // Check against the y-websocket server
    try {
      const result = await axios({
        method: 'get',
        url: `${yWebsocketUrl}/checkStatus`
      });
      // If it comes back as a 2xx code, it's fine. 503 means the MongoDB is broken.
      // If it doesn't connect at all, the axios call throws a connection error and
      // we don't need to throw our own.
      if (`${result.status}`[0] !== '2') {
        throw result.status;
      }
    } catch (e) {
      // Catch both the explicitly thrown non-2xx error or, more likely,
      // an axios-thrown network error (i.e., can't reach the server)
      toast(
        <div>
          Something went wrong. Try refreshing the page or{' '}
          <a href="mailto:support@contently.com">submit a ticket</a>
        </div>,
        {
          toastId: 'content server connection error',
          type: 'error'
        }
      );
      // Undisable the button so they can try clicking it again
      event.target.classList.remove('disabled');
      event.target.classList.remove('disabled-spinner');
      return false;
    }
    // If we get here, it means the ping to yWebsocketUrl came back good.
    // "unprevent" the default behavior by removing this event listener and reclicking it
    event.target.removeEventListener('click', checkContentServerBeforeEditing);
    event.target.click();
    return false;
  });

  // If there's something Edit-button shaped on the page, add a hook on it to check for a working
  // content server before going to the edit page.
  useEffect(() => {
    const editButtons = Array.from(document.getElementsByClassName('edit-button'));
    editButtons.forEach(editButton =>
      editButton.addEventListener('click', checkContentServerBeforeEditing)
    );
    // Cleanup when the component unmounts
    return () =>
      editButtons.forEach(editButton =>
        editButton.removeEventListener('click', checkContentServerBeforeEditing)
      );
  }, []);

  const portalTarget = document.getElementById('currently-editing');

  return (
    <Fragment>
      <ShowPageWrapper storyId={storyId} yWebsocketUrl={yWebsocketUrl} jwt={jwt}>
        <StoryContent storyId={storyId} customFields={customFields} readOnlyMode />
      </ShowPageWrapper>
      {portalTarget && createPortal(<CurrentlyEditingUsers storyId={storyId} />, portalTarget)}
    </Fragment>
  );
}

ShowPage.propTypes = {
  appData: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/client/pages/ShowPage.jsx